/**
 * MantaMaterial — the wing wave plus this game's underwater lighting.
 *
 * The vertex stage is vendored: world/manta/voxelManta.js ships `MANTA_WAVE_GLSL`, a
 * travelling wave that runs outward along the span and backward along the chord at the same
 * time. That double lag is the whole animal — freeze either and a flexible membrane becomes a
 * rigid plate hinged at the shoulder.
 *
 * The fragment stage is ours, so a manta is lit by the same sun as the reef it flies over.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { COAST_GLSL } from '../world/Coast.ts';
import { MANTA_WAVE_GLSL } from '../world/manta/voxelManta.js';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

const VERT = /* glsl */ `
${MANTA_WAVE_GLSL}
${COAST_GLSL}

uniform float uVox;
uniform float uDuneAmp;
uniform float uReefRise;
uniform float uGround;

attribute vec3 aRest;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;

void main() {
  vec3 centre = aRest;
  if (aPart < 0.5)      discPos(aS, aUV, aRest, centre);
  else if (aPart < 2.5) lobePos(aPart, aS, aUV, aRest, centre);
  else                  tailPos(aS, aUV, aRest, centre);

  /* Drape. A manta is a membrane, so unlike the turtle's rigid shell the whole animal may
     conform: a wingtip that would pass through a reef spur is lifted onto it instead. At
     altitude — which is most of the time for this animal — the term is exactly zero. */
  vec4 cw = modelMatrix * vec4(centre, 1.0);
  float lift = max(0.0, bedY(cw.xz, uDuneAmp, uReefRise) + uGround - cw.y);

  vec4 world = modelMatrix * vec4(position * uVox + centre, 1.0);
  world.y += lift;
  vWorldPos = world.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  vCol = aCol;
  vRole = aRole;
  vShade = aShade;

  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbientTop;
uniform vec3  uAmbientBottom;
uniform vec3  uRimColor;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform float uTime;
uniform float uPatch;
uniform float uSpots;
uniform float uTone;
uniform sampler2D uShadowMap;
uniform mat4      uShadowMatrix;
uniform float     uShadowStrength;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;

float lattice(vec2 p) {
  return (sin(p.x) + sin(p.y * 0.87 + 1.7) + sin((p.x + p.y) * 0.62 + 3.1)) * 0.3333333;
}
float dapple(vec2 w, float t) {
  float a = 1.0 - abs(lattice(w * 0.55 + vec2( 0.31, -0.22) * t));
  float b = 1.0 - abs(lattice(w * 1.27 + vec2(-0.19,  0.42) * t));
  return pow(clamp(min(a, b), 0.0, 1.0), 5.0);
}

float shadowAt(vec3 world, vec3 n, float ndl) {
  vec4 lp = uShadowMatrix * vec4(world + n * 0.12, 1.0);
  vec3 c = lp.xyz / lp.w;
  c = c * 0.5 + 0.5;
  if (c.x < 0.0 || c.x > 1.0 || c.y < 0.0 || c.y > 1.0 || c.z > 1.0) return 1.0;
  float bias = mix(0.0018, 0.0004, clamp(ndl, 0.0, 1.0));
  float texel = 1.0 / 2048.0;
  float lit = 0.0;
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel,  texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel,  texel)).r);
  lit *= 0.25;
  vec2 e = abs(c.xy - 0.5) * 2.0;
  return mix(1.0, lit, 1.0 - smoothstep(0.80, 1.0, max(e.x, e.y)));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 L = normalize(uSunDir);

  /* Every marking is BAKED into the vertex colour and then re-stated here from its role, so
     one geometry serves every individual: the shoulder chevron, the ventral spot pattern and
     the dorsal tone are each a uniform, not a rebuild. */
  vec3 base = vCol;
  if (vRole > 3.5 && vRole < 4.5)      base = vec3(0.028, 0.028, 0.036);   // eye
  else if (vRole > 4.5 && vRole < 5.5) base = vec3(0.042, 0.045, 0.055);   // mouth
  else if (vRole > 5.5 && vRole < 6.5) base = vec3(0.225, 0.235, 0.248);   // gill
  else if (vRole > 6.5)                base = vec3(0.030, 0.034, 0.044);   // rim
  else if (vRole > 2.5)                base = mix(vec3(0.10, 0.11, 0.13), base, uPatch);
  else if (vRole > 1.5)                base = mix(vec3(0.855, 0.872, 0.860), base, uSpots);
  else if (vRole < 0.5)                base *= mix(0.55, 1.85, uTone);     // dorsal
  base *= vShade;

  float ndl = dot(N, L) * 0.5 + 0.5;
  float shade = mix(1.0, shadowAt(vWorldPos, N, dot(N, L)), uShadowStrength);
  vec3 ambient = mix(uAmbientBottom, uAmbientTop, N.y * 0.5 + 0.5);
  vec3 col = base * (ambient + uSunColor * (ndl * ndl) * shade);

  float up = smoothstep(0.12, 0.8, N.y);
  col += mix(base, vec3(1.0), 0.35) * uSunColor * (dapple(vWorldPos.xz, uTime) * up * 1.1 * shade);

  float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
  col += uRimColor * (fres * 0.75);

  float fd = uFogDensity * length(cameraPosition - vWorldPos);
  col = mix(col, uFogColor, clamp(1.0 - exp(-fd * fd), 0.0, 1.0));

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class MantaMaterial extends THREE.ShaderMaterial {
  constructor(voxelSize: number, worldScale = 1) {
    super({
      name: 'MantaMaterial',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        // --- wing wave. uStroke is INTEGRATED on the CPU, never uTime times a live rate.
        uStroke: { value: 0 },
        uAmp: { value: 0.30 },
        uFlex: { value: 1.7 },
        // The two lags ARE the animal. Span lag makes the tip trail the root, chord lag makes
        // the trailing edge trail the leading edge; zero either and it flaps like a board.
        uSpanLag: { value: 1.35 },
        uChordLag: { value: 0.85 },
        uTwist: { value: 0.42 },
        uLead: { value: -Math.PI / 2 },
        uW: { value: 1.10 },
        uBank: { value: 0 },
        uLobe: { value: 0 },
        uTailAmp: { value: 0.10 },
        uVox: { value: voxelSize },
        uDuneAmp: { value: OCEAN.duneAmplitude },
        uReefRise: { value: OCEAN.reefRise },
        uGround: { value: voxelSize * worldScale * 0.5 },

        // --- markings
        uPatch: { value: 0.72 },
        uSpots: { value: 1.0 },
        uTone: { value: 0.30 },

        // --- the scene's own lighting vocabulary
        uTime: { value: 0 },
        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: srgb(WATER.sunColor) },
        uAmbientTop: { value: srgb(WATER.ambientTop) },
        uAmbientBottom: { value: srgb(WATER.ambientBottom) },
        uRimColor: { value: srgb(WATER.rimColor) },
        uFogColor: { value: srgb(WATER.fogColor) },
        uFogDensity: { value: WATER.fogDensity },
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        uShadowStrength: { value: 0 },
      },
      side: THREE.FrontSide,
      transparent: false,
      fog: false,
    });
  }

  /** a depth twin for the shadow pass, sharing uniforms BY REFERENCE so the pose matches */
  makeShadowMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      name: 'MantaMaterial:depth',
      vertexShader: VERT,
      fragmentShader: 'void main() { gl_FragColor = vec4(1.0); }',
      uniforms: this.uniforms,
      side: THREE.FrontSide,
      fog: false,
    });
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.uniforms, env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }
}
